import { useEffect } from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Faq } from "@/components/Faq";
import { BookButton, SecondaryButton } from "@/components/CtaButtons";
import { SERVICES } from "@/data/services";
import { serviceLd, faqLd, breadcrumbLd } from "@/lib/seo";
import { INTEREST_TO_REASON } from "@/lib/cta";
import { ChevronRight, Check } from "lucide-react";

export default function ServicePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const s = SERVICES[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!s) return <Navigate to="/" replace />;

  const reason = INTEREST_TO_REASON[s.interest];
  const jsonLd = [
    serviceLd(s),
    faqLd(s.faq),
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Expertise", path: "/#expertise" }, { name: s.navLabel, path: `/${s.slug}` }]),
  ];

  return (
    <div data-testid={`service-page-${s.slug}`}>
      <Seo title={s.seoTitle} description={s.metaDescription} path={`/${s.slug}`} jsonLd={jsonLd} />
      <Header />
      <main className="pt-32 pb-28 px-6 md:px-12 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted2 mb-10" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-gold">{s.navLabel}</span>
          </nav>

          <p className="font-mono text-[11px] tracking-[0.35em] text-gold uppercase mb-6">{s.serviceType}</p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-[1.05] mb-8">{s.h1}</motion.h1>
          <p className="text-bone/90 text-xl leading-relaxed max-w-3xl mb-10 font-light">{s.lead}</p>

          <div className="flex flex-col sm:flex-row gap-3 mb-20">
            <BookButton reason={reason} size="lg" testid="service-book" />
            <SecondaryButton to="/?interest=general#contact" label="Send an Enquiry" testid="service-enquiry" arrow={false} />
          </div>

          <section className="mb-16">
            <h2 className="font-serif font-light text-3xl md:text-4xl mb-6">The challenge</h2>
            <p className="text-muted2 text-lg leading-relaxed max-w-3xl">{s.problem}</p>
          </section>

          <section className="mb-16">
            <h2 className="font-serif font-light text-3xl md:text-4xl mb-10">How I help</h2>
            <div className="border-t border-white/15">
              {s.approach.map((a, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="border-b border-white/15 py-7 flex items-start gap-6" data-testid={`approach-${i}`}>
                    <span className="font-mono text-sm text-gold/70 shrink-0 pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl font-light mb-2">{a.t}</h3>
                      <p className="text-muted2 leading-relaxed">{a.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-serif font-light text-3xl md:text-4xl mb-8">{s.scopeTitle}</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {s.scope.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-bone/90" data-testid={`scope-${i}`}>
                  <Check size={18} className="text-gold shrink-0 mt-1" /> <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-20 border-l border-gold/40 pl-8">
            <h2 className="font-serif font-light text-2xl md:text-3xl mb-4">Relevant experience</h2>
            <p className="text-muted2 leading-relaxed max-w-3xl">{s.credibility}</p>
          </section>

          <section className="mb-20">
            <Faq items={s.faq} title="Frequently asked questions" testidPrefix="service-faq" />
          </section>

          <section className="mb-16 bg-surface border border-white/12 p-8 md:p-12">
            <h2 className="font-serif font-light text-2xl md:text-3xl mb-4">Ready to discuss this?</h2>
            <p className="text-muted2 mb-8 max-w-2xl">Book a complimentary 20-minute introductory call to talk it through and determine the right next step.</p>
            <BookButton reason={reason} size="lg" testid="service-book-footer" />
          </section>

          <div className="pt-8 border-t border-white/15">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-6">Related expertise</p>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {s.related.map((r) => (
                <Link key={r} to={`/${r}`} data-testid={`related-${r}`} className="font-serif text-lg hover:text-gold transition-colors hover-underline">
                  {SERVICES[r].navLabel}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
